import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/kit'
import {
  FieldContent,
  FieldDescription,
  FieldLabel,
  Field as FieldLayout,
} from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import type {
  CategoryDTO,
  CategoryIconKey,
} from '@/modules/products/domain/contracts'
import { DEFAULT_CATEGORY_ICON_KEY } from '../lib/category-icons'
import { CategoryIconPicker } from './CategoryIconPicker'

export type CategoryFormValues = {
  name: string
  description: string
  iconKey: CategoryIconKey
}

type CategoryDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  initial?: Pick<CategoryDTO, 'name' | 'description' | 'iconKey'> | null
  saving?: boolean
  onSubmit: (values: CategoryFormValues) => void
}

const emptyForm = (): CategoryFormValues => ({
  name: '',
  description: '',
  iconKey: DEFAULT_CATEGORY_ICON_KEY,
})

export function CategoryDialog({
  open,
  onOpenChange,
  mode,
  initial,
  saving,
  onSubmit,
}: CategoryDialogProps) {
  const [form, setForm] = useState<CategoryFormValues>(emptyForm)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && initial) {
      setForm({
        name: initial.name,
        description: initial.description ?? '',
        iconKey: initial.iconKey ?? DEFAULT_CATEGORY_ICON_KEY,
      })
    } else {
      setForm(emptyForm())
    }
  }, [open, mode, initial])

  const canSave = form.name.trim().length > 0

  const handleSubmit = () => {
    if (!canSave) return
    onSubmit({
      name: form.name.trim(),
      description: form.description.trim(),
      iconKey: form.iconKey,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'ແກ້ໄຂຫມວດຫມູ່' : 'ເພີ່ມຫມວດຫມູ່ໃໝ່'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <FieldLayout>
            <FieldLabel htmlFor="category-name">ຊື່ຫມວດຫມູ່</FieldLabel>
            <FieldContent>
              <Input
                id="category-name"
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="ລູກປັດ, ຮິບບິ້ນ..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSubmit()
                  }
                }}
              />
            </FieldContent>
          </FieldLayout>

          <FieldLayout>
            <FieldLabel htmlFor="category-description">ຄຳອະທິບາຍ</FieldLabel>
            <FieldDescription>
              ສະແດງໃນເວັບໄຊຕ໌ (ຫມວດແນະນຳ) — ສั้นสุด 500 ຕົວອັກສອນ
            </FieldDescription>
            <FieldContent>
              <Textarea
                id="category-description"
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="ອະທິບາຍສั้นໆ ສຳລັບລູກຄ້າ..."
                rows={3}
                maxLength={500}
                className="min-h-20 resize-y"
              />
            </FieldContent>
          </FieldLayout>

          <FieldLayout>
            <FieldLabel>ໄອຄອນ</FieldLabel>
            <FieldDescription>
              Phosphor regular — ໃຊ້ໃນໜ້າແຮກ ແລະ catalog
            </FieldDescription>
            <FieldContent>
              <CategoryIconPicker
                value={form.iconKey}
                onChange={(iconKey) =>
                  setForm((prev) => ({ ...prev, iconKey }))
                }
              />
            </FieldContent>
          </FieldLayout>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            className="rounded-full"
            isLoading={saving}
            disabled={!canSave}
            onClick={handleSubmit}
          >
            ບັນທຶກ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
